import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { AuthenticationResponseJSON } from "@simplewebauthn/browser";
import { useAuth } from "@/providers/auth-provider";
import { startAuthentication } from "@simplewebauthn/browser";
import type { PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import {
  mfaChallenge,
  mfaRecovery,
  mfaTotp,
  mfaVerify,
  passwordlessChallenge,
  passwordlessLogin,
  type LoginResponse,
  type MfaChallengeResponse,
  type MfaRecoveryResponse,
} from "../api/auth.api";
import { ApiError } from "@/lib/api/client";
import { preparedCeremony, type CeremonyOwner } from "@/lib/webauthn";
import { resolvePostLoginPath } from "../post-login-redirect";

export function useMfaChallenge() {
  return useMutation<MfaChallengeResponse, ApiError, string>({
    networkMode: "always",
    mutationFn: mfaChallenge,
  });
}

export function useMfaVerify() {
  return useMutation<
    LoginResponse,
    ApiError,
    { pendingToken: string; response: AuthenticationResponseJSON }
  >({
    networkMode: "always",
    mutationFn: ({ pendingToken, response }) => mfaVerify(pendingToken, response),
  });
}

export function useMfaRecovery() {
  return useMutation<MfaRecoveryResponse, ApiError, { pendingToken: string; code: string }>({
    networkMode: "always",
    mutationFn: ({ pendingToken, code }) => mfaRecovery(pendingToken, code),
  });
}

export function useMfaTotp() {
  return useMutation<LoginResponse, ApiError, { pendingToken: string; code: string }>({
    networkMode: "always",
    mutationFn: ({ pendingToken, code }) => mfaTotp(pendingToken, code),
  });
}

export type PasswordlessLoginPhase = "idle" | "cancelable" | "verifying";

export function usePasswordlessLogin(ceremonyOwner: CeremonyOwner) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const [phase, setPhase] = useState<PasswordlessLoginPhase>("idle");

  const mutation = useMutation<LoginResponse, ApiError | Error, void>({
    networkMode: "always",
    mutationFn: async () => {
      setPhase("cancelable");
      try {
        const assertion = await preparedCeremony(
          () => passwordlessChallenge(),
          ({ options }) =>
            startAuthentication({
              optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON,
            }),
          { owner: ceremonyOwner },
        );
        setPhase("verifying");
        return await passwordlessLogin(assertion);
      } finally {
        setPhase("idle");
      }
    },
    onSuccess: (data) => {
      login(data.accessKey);
      navigate({ to: resolvePostLoginPath(search.redirect) });
    },
  });

  return { ...mutation, phase };
}
